import React, { useState, useEffect } from 'react'
import { Row, Col, Card } from 'react-bootstrap'
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts'
import api from 'api/api'
import { toast } from 'react-toastify'
import { formatNumberForDisplay } from 'utils/numberUtils'
import { getServerDate, startOfMonth, endOfMonth, getShortDisplayDate } from 'utils/date'
import { LoadingSpinner } from 'components/LoadingSpinner'
import { DateRangeSelector } from 'components/DateRangeSelector'
import { DownloadButton } from 'components/DownloadButton'
import { API_URL } from 'config/general'


const VentasPorRango = () => {
	const [loading, setLoading] = useState(false)
	const [fechaInicio, setFechaInicio] = useState(getServerDate(startOfMonth()))
	const [fechaFin, setFechaFin] = useState(getServerDate(endOfMonth()))
	const [datos, setDatos] = useState(null)
	const [chartData, setChartData] = useState([])

	const cargarDatos = async (inicio, fin) => {
		try {
			setLoading(true)
			const response = await api.get(`a/reportes/ventas?fechaInicio=${inicio}&fechaFin=${fin}`)

			if (response.error) {
				toast.error(response.msg || 'Error al cargar datos')
				return
			}

			setDatos(response.datos)
			
			const processed = response.datos.ventasPorDia.map(dia => ({
				fecha: getShortDisplayDate(dia.fecha, 'never'),
				monto: dia.monto,
				pedidos: dia.numPedidos
			}))
			setChartData(processed)

		} catch (error) {
			console.error('Error:', error)
			toast.error('Error al cargar el reporte')
		} finally {
			setLoading(false)
		}
	}

	useEffect(() => {
		cargarDatos(fechaInicio, fechaFin)
	}, [])

	const handleRangeChange = (inicio, fin) => {
		setFechaInicio(inicio)
		setFechaFin(fin)
	}

	const handleConsultar = (inicio, fin) => {
		cargarDatos(inicio, fin)
	}

	const formatMonto = (value) => {
		return formatNumberForDisplay(value.toString()) + ' Gs.'
	}

	return (
		<div className="ventas-por-rango">
			<DateRangeSelector
				fechaInicio={new Date(fechaInicio.replace(/-/g, '/'))}
				fechaFin={new Date(fechaFin.replace(/-/g, '/'))}
				onRangeChange={handleRangeChange}
				onConsultar={handleConsultar}
			/>

			{loading ? (
				<div className="d-flex justify-content-center py-5">
					<LoadingSpinner />
				</div>
			) : datos ? (
				<>
					<Row className="mb-4">
						<Col lg={4} md={6} className="mb-3">
							<Card className="h-100 metric-card metric-card-primary">
								<Card.Body className="text-center">
									<div className="metric-icon">
										<i className="fa fa-shopping-cart"></i>
									</div>
									<div className="metric-label">Total Pedidos</div>
									<div className="metric-value text-primary">
										{datos.totalPedidos}
									</div>
								</Card.Body>
							</Card>
						</Col>

						<Col lg={4} md={6} className="mb-3">
							<Card className="h-100 metric-card metric-card-success">
								<Card.Body className="text-center">
									<div className="metric-icon">
										<i className="fa fa-dollar-sign"></i>
									</div>
									<div className="metric-label">Monto Total</div>
									<div className="metric-value text-success">
										{formatMonto(datos.totalMonto)}
									</div>
								</Card.Body>
							</Card>
						</Col>

						<Col lg={4} md={6} className="mb-3">
							<Card className="h-100 metric-card metric-card-purple">
								<Card.Body className="text-center">
									<div className="metric-icon">
										<i className="fa fa-receipt"></i>
									</div>
									<div className="metric-label">Ticket Promedio</div>
									<div className="metric-value text-purple">
										{formatMonto(datos.ticketPromedio)}
									</div>
								</Card.Body>
							</Card>
						</Col>
					</Row>

					<Card className="mb-4">
						<Card.Header className="d-flex justify-content-between align-items-center">
							<h5 className="mb-0">
								<i className="fa fa-chart-bar me-2 text-primary"></i>
								Ventas por día
							</h5>
							<DownloadButton
								url={`${API_URL}a/reportes/ventas/excel?fechaInicio=${fechaInicio}&fechaFin=${fechaFin}`}
								whileDownloading={<><i className="fa fa-spinner fa-spin me-2"></i>Generando...</>}
								variant="success"
								size="sm"
							>
								<i className="fa fa-file-excel me-2"></i>
								Descargar Excel
							</DownloadButton>
						</Card.Header>
						<Card.Body>
							{chartData.length > 0 ? (
								<ResponsiveContainer width="100%" height={350}>
									<BarChart
										data={chartData}
										margin={{ top: 20, right: 30, bottom: 60, left: 20 }}
									>
										<CartesianGrid strokeDasharray="3 3" />
										<XAxis
											dataKey="fecha"
											angle={-45}
											textAnchor="end"
											height={70}
											tick={{ fontSize: 12 }}
											interval={Math.ceil(chartData.length / 15)}
										/>
										<YAxis
											tickFormatter={(value) => formatNumberForDisplay(value.toString())}
										/>
										<Tooltip
											formatter={(value) => formatMonto(value)}
											labelFormatter={(label) => `Fecha: ${label}`}
										/>
										<Bar
											dataKey="monto"
											fill="#0d6efd"
											name="Monto"
											radius={[4, 4, 0, 0]}
										/>
									</BarChart>
								</ResponsiveContainer>
							) : (
								<div className="text-center text-muted py-5">
									<i className="fa fa-chart-bar fa-3x mb-3"></i>
									<p>No hay datos para el período seleccionado</p>
								</div>
							)}
						</Card.Body>
					</Card>
				</>
			) : (
				<div className="text-center text-muted py-5">
					<i className="fa fa-search fa-3x mb-3"></i>
					<p>Seleccione un rango de fechas</p>
				</div>
			)}
		</div>
	)
}

export default VentasPorRango